import { useMutation } from "@tanstack/react-query";
import { ArrowUpRight, Check, Camera, KeyRound, Monitor, Moon, ShieldCheck, Sun, Trash2, UserRound } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AccountClient, PasswordClient, avatarSrc, initials, languageOf } from "../../api";
import { useAuth } from "../../auth";
import { setLocale, SUPPORTED_LOCALES, type Locale } from "../../i18n";
import { useTheme, type ThemePreference } from "../../theme";
import { Avatar, Badge, Button, Field, Input, cn, setRecordLinksInNewTab, Switch, toast, useRecordLinksInNewTab } from "../../ui";
import { EditPane } from "../../ui/EditPane";
import { PageHeader } from "../PageHeader";

/** Longest edge of a stored avatar. See `decodeAndSquare`. */
const AVATAR_PX = 256;
const MIN_PASSWORD_LENGTH = 6;

/**
 * The signed-in person's own account.
 *
 * Everything here is either theirs to change or theirs to know. That split is
 * the page's organising idea rather than an afterthought: the first three tabs
 * are settings, the last is a statement of what this account can reach, shown
 * because "why can't I see that screen?" is the question this page exists to
 * answer and nobody can answer it from the nav alone.
 *
 * Reached from the avatar in the top bar, which is the only affordance most
 * people will ever look for.
 */
export function AccountPage() {
  const { t } = useTranslation();
  const { account, reload, canWrite } = useAuth();

  if (!account) return null;

  const picture = avatarSrc(account.data?.avatar);

  return (
    <>
      <PageHeader
        title={account.credentials.username}
        subtitle={account.credentials.email}
        badges={
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={account.level === "USER" ? "neutral" : "accent"} size="sm">
              {account.level}
            </Badge>
            {!canWrite && (
              <Badge variant="warning" size="sm">
                {t("badge.read-only", "Read only")}
              </Badge>
            )}
          </div>
        }
      />

      <EditPane
        header={<Identity picture={picture} />}
        tabs={[
          {
            id: "profile",
            label: t("account.tab.profile", "Profile"),
            content: <ProfileTab picture={picture} onSaved={reload} />,
          },
          {
            id: "preferences",
            label: t("account.tab.preferences", "Preferences"),
            content: <PreferencesTab onSaved={reload} />,
          },
          {
            id: "security",
            label: t("account.tab.security", "Security"),
            content: <SecurityTab />,
          },
          {
            id: "access",
            label: t("account.tab.access", "Access"),
            badge: <Badge size="sm">{account.view_codes.length}</Badge>,
            content: <AccessTab />,
          },
        ]}
      />
    </>
  );
}

// ── identity strip ────────────────────────────────────────────────────────

function Identity({ picture }: { picture: string | null }) {
  const { t } = useTranslation();
  const { account } = useAuth();
  if (!account) return null;

  const facts: Array<{ label: string; value: ReactNode }> = [
    { label: t("column.username", "Username"), value: account.credentials.username },
    { label: t("form.field.email", "Email"), value: account.credentials.email },
    { label: t("column.creation-date", "Created"), value: formatDay(account.created_at) },
    {
      label: t("badge.last-login", "Last login"),
      value: formatMoment(account.logged_at) || t("value.never", "Never"),
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
      <div className="flex items-center gap-3">
        <Avatar src={picture} initials={initials(account)} size="lg" />
        <div className="flex flex-col leading-tight">
          <span className="text-[15px] font-semibold text-ink">{account.credentials.username}</span>
          <span className="text-[13px] text-ink-muted">{account.credentials.email}</span>
        </div>
      </div>

      <dl className="flex flex-wrap gap-x-8 gap-y-3">
        {facts.map((fact) => (
          <div key={fact.label} className="flex flex-col gap-0.5">
            <dt className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              {fact.label}
            </dt>
            <dd className="text-[13px] text-ink">{fact.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ── profile ───────────────────────────────────────────────────────────────

function ProfileTab({ picture, onSaved }: { picture: string | null; onSaved: () => Promise<unknown> }) {
  const { t } = useTranslation();
  const { account, http } = useAuth();
  const client = useMemo(() => new AccountClient(http), [http]);
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  if (!account) return null;

  const write = async (avatar: string) => {
    setBusy(true);
    try {
      await client.updateProfile(account.id, { avatar });
      await onSaved();
      toast.success(t("notification.save.success", "Item saved successfully"));
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.save.error", "Failed to save item"),
      );
    } finally {
      setBusy(false);
    }
  };

  const pick = async (file: File) => {
    try {
      await write(await decodeAndSquare(file, AVATAR_PX));
    } catch {
      toast.error(t("account.avatar.unreadable", "That file could not be read as an image"));
    }
  };

  return (
    <Section
      icon={<Camera className="size-4" />}
      title={t("account.avatar.title", "Profile picture")}
      description={t(
        "account.avatar.description",
        "Shown in the top bar and anywhere your account appears. Cropped to a square and scaled down before it is stored, so any size of file is fine.",
      )}
    >
      <div className="flex flex-wrap items-center gap-5">
        <Avatar src={picture} initials={initials(account)} size="xl" />
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="primary"
            loading={busy}
            icon={<Camera className="size-4" />}
            onClick={() => input.current?.click()}
          >
            {picture ? t("account.avatar.replace", "Replace") : t("account.avatar.upload", "Upload")}
          </Button>
          {picture && (
            <Button
              variant="ghost"
              disabled={busy}
              icon={<Trash2 className="size-4" />}
              onClick={() => void write("")}
            >
              {t("button.remove", "Remove")}
            </Button>
          )}
        </div>
      </div>

      <input
        ref={input}
        type="file"
        hidden
        accept="image/png,image/jpeg,image/gif,image/webp"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void pick(file);
        }}
      />

      <div className="grid max-w-2xl gap-4 pt-2 sm:grid-cols-2">
        <Field label={t("column.username", "Username")}>
          <Input value={account.credentials.username} readOnly disabled />
        </Field>
        <Field label={t("form.field.email", "Email")}>
          <Input value={account.credentials.email} readOnly disabled />
        </Field>
      </div>
      <p className="text-[12px] text-ink-faint">
        {t(
          "account.identity.admin-only",
          "Your username and email are set by an administrator.",
        )}
      </p>
    </Section>
  );
}

// ── preferences ───────────────────────────────────────────────────────────

function PreferencesTab({ onSaved }: { onSaved: () => Promise<unknown> }) {
  const { t, i18n } = useTranslation();
  const { account, http } = useAuth();
  const { preference, setPreference } = useTheme();
  const newTab = useRecordLinksInNewTab();
  const client = useMemo(() => new AccountClient(http), [http]);
  const [busy, setBusy] = useState(false);

  if (!account) return null;

  const current = (i18n.language.split("-")[0] ?? "en") as Locale;

  const chooseLanguage = async (locale: Locale) => {
    // Applied first so the page answers immediately; the write is what makes it
    // survive to the next device.
    await setLocale(locale);
    setBusy(true);
    try {
      await client.updateProfile(account.id, { language: languageOf(locale) });
      await onSaved();
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.save.error", "Failed to save item"),
      );
    } finally {
      setBusy(false);
    }
  };

  const themes: Array<{ value: ThemePreference; label: string; icon: ReactNode }> = [
    { value: "system", label: t("theme.system", "System"), icon: <Monitor className="size-4" /> },
    { value: "light", label: t("theme.light", "Light"), icon: <Sun className="size-4" /> },
    { value: "dark", label: t("theme.dark", "Dark"), icon: <Moon className="size-4" /> },
  ];

  return (
    <div className="flex flex-col gap-8">
      <Section
        icon={<UserRound className="size-4" />}
        title={t("language.label", "Language")}
        description={t(
          "account.language.description",
          "Applies immediately and is stored on your account, so it follows you to any of the three apps and any browser.",
        )}
      >
        <Choices
          options={SUPPORTED_LOCALES.map((locale) => ({
            value: locale,
            label: locale === "fr" ? "Français" : "English",
            hint: locale.toUpperCase(),
          }))}
          value={current}
          disabled={busy}
          onChange={(value) => void chooseLanguage(value as Locale)}
        />
      </Section>

      <Section
        icon={<Sun className="size-4" />}
        title={t("theme.label", "Theme")}
        description={t(
          "account.theme.description",
          "Kept on this device rather than on your account. A theme belongs to the screen you are sitting at, and the right answer on a laptop at night is rarely the right one on an office monitor. System follows your operating system.",
        )}
      >
        <Choices
          options={themes.map((entry) => ({
            value: entry.value,
            label: entry.label,
            icon: entry.icon,
          }))}
          value={preference}
          onChange={(value) => setPreference(value as ThemePreference)}
        />
      </Section>

      <Section
        icon={<ArrowUpRight className="size-4" />}
        title={t("account.links.title", "Record links")}
        description={t(
          "account.links.description",
          "The arrow beside a line (a component, an order, a supplier) opens that record. On, it opens in a new tab so the page you are on stays where it is. Clicking a line's own link is not affected.",
        )}
      >
        <Switch
          checked={newTab}
          onChange={setRecordLinksInNewTab}
          label={newTab ? t("account.links.new-tab", "Open in a new tab") : t("account.links.same-tab", "Open in this tab")}
        />
      </Section>
    </div>
  );
}

// ── security ──────────────────────────────────────────────────────────────

function SecurityTab() {
  const { t } = useTranslation();
  const { account, http } = useAuth();
  const client = useMemo(() => new PasswordClient(http), [http]);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const change = useMutation({
    mutationFn: () =>
      client.change(account!.id, { current_password: current, new_password: next }),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success(t("account.password.changed", "Password changed"));
    },
    onError: (failure) =>
      toast.error(
        // The endpoint answers 403 for a wrong current password, which as a
        // bare status reads like a permissions problem rather than a typo.
        (failure as { status?: number })?.status === 403
          ? t("account.password.wrong-current", "That is not your current password")
          : failure instanceof Error
            ? failure.message
            : t("notification.save.error", "Failed to save item"),
      ),
  });

  if (!account) return null;

  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== next;
  const ready =
    current.length > 0 && next.length >= MIN_PASSWORD_LENGTH && confirm === next && !change.isPending;

  return (
    <Section
      icon={<KeyRound className="size-4" />}
      title={t("account.password.title", "Change password")}
      description={t(
        "account.password.description",
        "Your current password is required, so that an unattended session cannot be used to lock you out of your own account.",
      )}
    >
      <form
        className="flex max-w-sm flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) change.mutate();
        }}
      >
        <Field label={t("account.password.current", "Current password")} required>
          <Input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>
        <Field
          label={t("account.password.new", "New password")}
          required
          error={
            tooShort
              ? t("form.validation.field.length", "Value must be at least {{min}} characters", {
                  min: MIN_PASSWORD_LENGTH,
                })
              : undefined
          }
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
        </Field>
        <Field
          label={t("account.password.confirm", "Confirm new password")}
          required
          error={mismatch ? t("account.password.mismatch", "The two do not match") : undefined}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>
        <div>
          <Button type="submit" variant="primary" disabled={!ready} loading={change.isPending}>
            {t("account.password.submit", "Change password")}
          </Button>
        </div>
      </form>
    </Section>
  );
}

// ── access ────────────────────────────────────────────────────────────────

function AccessTab() {
  const { t } = useTranslation();
  const { account, canWrite } = useAuth();
  if (!account) return null;

  const isRoot = account.level === "ROOT";

  return (
    <div className="flex flex-col gap-8">
      <Section
        icon={<ShieldCheck className="size-4" />}
        title={t("account.access.title", "What this account can do")}
        description={t(
          "account.access.description",
          "Set by an administrator and shown here so it is possible to tell a missing permission from a missing feature.",
        )}
      >
        <dl className="grid max-w-2xl gap-4 sm:grid-cols-3">
          <Fact label={t("column.level", "Level")}>
            <Badge variant={account.level === "USER" ? "neutral" : "accent"}>{account.level}</Badge>
          </Fact>
          <Fact label={t("account.access.write", "Can make changes")}>
            <Badge variant={canWrite ? "success" : "warning"}>
              {canWrite ? t("value.yes", "Yes") : t("value.no", "No")}
            </Badge>
          </Fact>
          <Fact label={t("column.status", "Status")}>
            <Badge variant={account.status === "ACTIVE" ? "success" : "error"}>
              {account.status}
            </Badge>
          </Fact>
        </dl>
      </Section>

      <Section
        icon={<Check className="size-4" />}
        title={t("account.access.views", "Screens")}
        description={
          isRoot
            ? t(
                "account.access.root",
                "A ROOT account bypasses the per-screen list entirely and reaches everything.",
              )
            : t(
                "account.access.views-description",
                "The screens this account may open. Anything not listed is hidden from the navigation rather than refused on arrival.",
              )
        }
      >
        {isRoot ? (
          <Badge variant="accent">{t("account.access.everything", "Every screen")}</Badge>
        ) : account.view_codes.length === 0 ? (
          <p className="text-[13px] text-ink-muted">
            {t("account.access.none", "No screens are assigned to this account.")}
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {[...account.view_codes].sort().map((code) => (
              <Badge key={code} variant="neutral" size="sm" className="tabular">
                {code}
              </Badge>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

// ── shared bits ───────────────────────────────────────────────────────────

function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-[14px] font-semibold text-ink">
          <span className="text-ink-faint">{icon}</span>
          {title}
        </h2>
        <p className="max-w-2xl text-[13px] leading-relaxed text-ink-muted">{description}</p>
      </header>
      {children}
    </section>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/**
 * A row of mutually exclusive cards.
 *
 * A `<select>` would do, but these are settings someone changes once and then
 * wants to confirm at a glance; showing all the options with the live one
 * marked answers "what is it set to" without a click.
 */
function Choices({
  options,
  value,
  disabled,
  onChange,
}: {
  options: Array<{ value: string; label: string; hint?: string; icon?: ReactNode }>;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={disabled}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex min-w-32 items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-left text-[13px]",
              "transition-colors disabled:pointer-events-none disabled:opacity-50",
              active
                ? "border-accent bg-accent-soft font-medium text-accent-strong"
                : "border-line bg-surface text-ink hover:bg-surface-sunk",
            )}
          >
            {option.icon && <span className={active ? "text-accent-strong" : "text-ink-faint"}>{option.icon}</span>}
            <span className="flex-1">{option.label}</span>
            {active ? (
              <Check className="size-4" />
            ) : (
              option.hint && <span className="text-[11px] text-ink-faint">{option.hint}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────

function formatDay(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function formatMoment(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Centre-crops a picked file to a square and re-encodes it small.
 *
 * Worth doing in the browser rather than server-side: the avatar lives in a
 * bytea column and rides along in every `/auth/me` response, so a phone photo
 * uploaded raw would be paid for on every page load of every app, forever. At
 * 256px JPEG this lands around 15 kB.
 */
async function decodeAndSquare(file: File, size: number): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("no 2d context");

    // JPEG has no alpha; without this a transparent PNG comes out black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, size, size);

    const edge = Math.min(bitmap.width, bitmap.height);
    context.drawImage(
      bitmap,
      (bitmap.width - edge) / 2,
      (bitmap.height - edge) / 2,
      edge,
      edge,
      0,
      0,
      size,
      size,
    );

    // The DTO carries bare Base64, so the `data:image/jpeg;base64,` prefix comes off.
    return canvas.toDataURL("image/jpeg", 0.85).split(",")[1] ?? "";
  } finally {
    bitmap.close();
  }
}
